import api from "./client";

// Internal Admin API
export const adminAPI = {
  // Users
  getUsers: async (params = {}) => {
    const response = await api.get("/admin/users", { params });
    return response.data;
  },
  getUser: async (id) => {
    const response = await api.get(`/admin/users/${id}`);
    return response.data;
  },
  updateUser: async (id, attrs) => {
    const response = await api.put(`/admin/users/${id}`, attrs);
    return response.data;
  },
  updateMemberships: async (id, memberships) => {
    const response = await api.put(`/admin/users/${id}/memberships`, { memberships });
    return response.data;
  },
  deleteUser: async (id) => {
    const response = await api.delete(`/admin/users/${id}`);
    return response.data;
  },
  addRole: async (id, role) => {
    const response = await api.post(`/admin/users/${id}/add_role`, { role });
    return response.data;
  },
  removeRole: async (id, role) => {
    const response = await api.post(`/admin/users/${id}/remove_role`, { role });
    return response.data;
  },
  // Billing Accounts
  // Metrics Gallery (curated templates)
  getGalleryMetrics: async () => {
    const response = await api.get("/admin/metrics_gallery");
    return response.data;
  },
  createGalleryMetric: async (metric) => {
    const response = await api.post("/admin/metrics_gallery", { metric });
    return response.data;
  },
  updateGalleryMetric: async (id, metric) => {
    const response = await api.put(`/admin/metrics_gallery/${id}`, { metric });
    return response.data;
  },
  deleteGalleryMetric: async (id) => {
    const response = await api.delete(`/admin/metrics_gallery/${id}`);
    return response.data;
  },

  getBillingAccounts: async ({ archived } = {}) => {
    const response = await api.get("/admin/billing_accounts", {
      params: archived === undefined ? {} : { archived },
    });
    return response.data;
  },
  getBillingAccount: async (id) => {
    const response = await api.get(`/admin/billing_accounts/${id}`);
    return response.data;
  },
  addCredits: async (id, amount, description, component = "manual_credit") => {
    const response = await api.post(`/admin/billing_accounts/${id}/add_credits`, { amount, description, component });
    return response.data;
  },
  removeCredits: async (id, amount, description) => {
    const response = await api.post(`/admin/billing_accounts/${id}/remove_credits`, { amount, description });
    return response.data;
  },
  getAccountTransactions: async (id, params = {}) => {
    const response = await api.get(`/admin/billing_accounts/${id}/transactions`, { params });
    return response.data;
  },
  getPurgePreview: async (id) =>
    (await api.get(`/admin/billing_accounts/${id}/purge_preview`)).data,

  deleteBillingAccount: async (id, { force = false } = {}) =>
    (await api.delete(`/admin/billing_accounts/${id}`, { params: { force } })).data,

  getPurgeStatus: async (auditId) =>
    (await api.get(`/admin/account_purges/${auditId}`)).data,

  // Reversible counterpart to delete: freezes the account and its workspaces
  // instead of destroying them.
  archiveBillingAccount: async (id, { reason } = {}) =>
    (await api.post(`/admin/billing_accounts/${id}/archive`, { reason })).data,

  restoreBillingAccount: async (id) =>
    (await api.post(`/admin/billing_accounts/${id}/restore`)).data,

  // Single-workspace archive/restore (reversible counterpart of the account-level
  // archive above, scoped to one Client under the billing account).
  getAccountWorkspaces: async (billingAccountId) =>
    (await api.get(`/admin/billing_accounts/${billingAccountId}/workspaces`)).data.data,

  archiveWorkspace: async (id, { reason } = {}) =>
    (await api.post(`/admin/workspaces/${id}/archive`, { reason })).data,

  restoreWorkspace: async (id) =>
    (await api.post(`/admin/workspaces/${id}/restore`)).data,

  // Pricing
  getPricingRules: async () => {
    const response = await api.get("/admin/pricing_rules");
    return response.data;
  },
  updatePricingRule: async (id, data) => {
    const response = await api.put(`/admin/pricing_rules/${id}`, { pricing_rule: data });
    return response.data;
  },
  createPricingRule: async (data) => {
    const response = await api.post("/admin/pricing_rules", { pricing_rule: data });
    return response.data;
  },
  deletePricingRule: async (id) => {
    const response = await api.delete(`/admin/pricing_rules/${id}`);
    return response.data;
  },

  // Plans CRUD
  getPlans: async () => (await api.get("/admin/plans")).data.data,
  getPlan: async (id) => (await api.get(`/admin/plans/${id}`)).data.data,
  createPlan: async (attrs) => (await api.post("/admin/plans", attrs)).data.data,
  updatePlan: async (id, attrs) => (await api.patch(`/admin/plans/${id}`, attrs)).data.data,
  archivePlan: async (id) => (await api.delete(`/admin/plans/${id}`)).data.data,
  syncPlanStripe: async (id) => (await api.post(`/admin/plans/${id}/sync_stripe`)).data.data,
  getScopedPlans: async (billingAccountId) =>
    (await api.get("/admin/plans/billing_account_scoped", { params: { billing_account_id: billingAccountId } })).data.data,

  // Subscription mgmt on billing accounts
  assignPlan: async (billingAccountId, planId) =>
    (await api.post(`/admin/billing_accounts/${billingAccountId}/assign_plan`, { plan_id: planId })).data.data,
  changePlan: async (billingAccountId, planId) =>
    (await api.post(`/admin/billing_accounts/${billingAccountId}/change_plan`, { plan_id: planId })).data.data,
  extendTrial: async (billingAccountId, days) =>
    (await api.post(`/admin/billing_accounts/${billingAccountId}/extend_trial`, { days })).data.data,
  forceRenew: async (billingAccountId) =>
    (await api.post(`/admin/billing_accounts/${billingAccountId}/force_renew`)).data.data,
  cancelBillingSubscription: async (billingAccountId, atPeriodEnd = true) =>
    (await api.post(`/admin/billing_accounts/${billingAccountId}/cancel_subscription`, { at_period_end: atPeriodEnd })).data.data,
  syncFromStripe: async (billingAccountId) =>
    (await api.post(`/admin/billing_accounts/${billingAccountId}/sync_from_stripe`)).data.data,

  // Per-account limit overrides (key: max_users | max_concurrent_runs | max_agents).
  // A blank/empty value clears the override, reverting to the plan default.
  setLimitOverride: async (billingAccountId, key, value) =>
    (await api.post(`/admin/billing_accounts/${billingAccountId}/set_limit_override`, { key, value })).data.data,

  // Manually provision an account. Mode A: { billing_account_id, workspace_name }.
  // Mode B: { owner_name, owner_email, workspace_name, plan_id }.
  provisionAccount: async (attrs) =>
    (await api.post("/admin/provisioned_accounts", attrs)).data.data,

  // Invoices
  getInvoices: async (billingAccountId) =>
    (await api.get(`/admin/billing_accounts/${billingAccountId}/invoices`)).data.data,
  markInvoicePaid: async (subscriptionId) =>
    (await api.post(`/admin/invoices/${subscriptionId}/mark_paid`)).data.data,

  // Stripe events
  getStripeEvents: async (params = {}) =>
    (await api.get("/admin/stripe_events", { params })).data.data,
  getStripeEvent: async (id) => (await api.get(`/admin/stripe_events/${id}`)).data.data,
  replayStripeEvent: async (id) => (await api.post(`/admin/stripe_events/${id}/replay`)).data.data,

  // Phone numbers
  getPhoneNumbers: async () => (await api.get("/admin/phone_numbers")).data.data,
  createPhoneNumber: async (attrs) =>
    (await api.post("/admin/phone_numbers", { phone_number: attrs })).data.data,
  deletePhoneNumber: async (id) => (await api.delete(`/admin/phone_numbers/${id}`)).data,
  fetchPhoneNumbersFromTwilio: async () =>
    (await api.post("/admin/phone_numbers/fetch_from_twilio")).data.data,
  assignPhoneNumber: async (id, clientId) =>
    (await api.post(`/admin/phone_numbers/${id}/assign`, { client_id: clientId })).data.data,
  unassignPhoneNumber: async (id) =>
    (await api.post(`/admin/phone_numbers/${id}/unassign`)).data.data,
  setPhoneNumberAsDefault: async (id) =>
    (await api.post(`/admin/phone_numbers/${id}/set_as_default`)).data.data,
  setPhoneNumberAsCarrierDefault: async (id) =>
    (await api.post(`/admin/phone_numbers/${id}/set_as_carrier_default`)).data.data,
  unsetPhoneNumberAsCarrierDefault: async (id) =>
    (await api.post(`/admin/phone_numbers/${id}/unset_carrier_default`)).data.data,
};

export const dispatchBreakersAPI = {
  list: () => api.get("/admin/dispatch_breakers").then((r) => r.data.data),
  resume: (lane) => api.post(`/admin/dispatch_breakers/${lane}/resume`).then((r) => r.data),
};

export const rateLimitsAPI = {
  list: () => api.get("/admin/llm_rate_limits").then((r) => r.data),
  update: (id, patch) => api.patch(`/admin/llm_rate_limits/${id}`, patch).then((r) => r.data),
};

// Call-concurrency ceilings: the global box limit plus per-TTS-vendor account quotas.
// Editable at runtime because vendor quotas move faster than releases.
export const callLimitsAPI = {
  list: () => api.get("/admin/call_concurrency_limits").then((r) => r.data),
  create: (body) => api.post("/admin/call_concurrency_limits", body).then((r) => r.data),
  update: (id, patch) =>
    api.patch(`/admin/call_concurrency_limits/${id}`, patch).then((r) => r.data),
  remove: (id) => api.delete(`/admin/call_concurrency_limits/${id}`).then((r) => r.data),
};
